/**
 * ThemeAI cost estimation (design spec #916 §7).
 *
 * Client-side fallback used until the Wave 1C /tasks/:id/estimate endpoint
 * lands (frontend degrades gracefully — see useThemeAIRuns). The formula is
 * a documented judgment call (spec §21 flags the exact token-estimation
 * formula as an open item to confirm with the backend team) — it is meant to
 * give the user a reasonable order-of-magnitude figure for the go/no-go
 * decision in the cost-confirmation dialog (§7.3), not a precise bill.
 */

import { formatCredits, toCredits } from '../tokenbudgets/model'
import type { MoneyUnit } from '../tokenbudgets/model'

// Blended GBP-per-million-token rate used for the estimate (roughly GPT-4.1
// class pricing — see PipelinePromptSettingsStep.tsx MODEL_PRICING for the
// per-model table used elsewhere in AutoCoder).
const BLENDED_INPUT_RATE_PER_MILLION = 1.5
const BLENDED_OUTPUT_RATE_PER_MILLION = 6

// Average words per free-text response, used when the caller doesn't have a
// real word count yet (e.g. estimating before data is fully loaded).
const DEFAULT_WORDS_PER_RESPONSE = 30

// Rough words -> tokens ratio (English prose).
const WORDS_TO_TOKENS = 1.35

// Fixed per-response overhead tokens for the code-frame instructions +
// classification scaffolding (grows with frame size).
const FRAME_OVERHEAD_TOKENS_PER_THEME = 3

// Output tokens per response for a bare classification (no explanation).
const BASE_OUTPUT_TOKENS_PER_RESPONSE = 12

// Extra output tokens per response when explanations are included (§10.1).
const EXPLANATION_OUTPUT_TOKENS_PER_RESPONSE = 60

// Extra output tokens per response per opted-in sentiment/emotion pass (§9).
const OPTIONAL_PASS_OUTPUT_TOKENS_PER_RESPONSE = 15

// Human-coding comparison reference rates (spec §7.2).
const HUMAN_CODE_FRAME_DEV_COST = 350 // midpoint of £200-500
const HUMAN_PER_RESPONSE_COST = 0.225 // midpoint of £0.15-0.30
const HUMAN_QC_RATE = 0.125 // midpoint of 10-15%
const HUMAN_RESPONSES_PER_DAY = 400 // rough throughput for the weeks estimate

export interface ThemeAICostEstimateInput {
  responseCount: number
  themeCount: number
  includeExplanations: boolean
  includeSentiment?: boolean
  includeEmotion?: boolean
  /** Average words per response, if known (falls back to a default). */
  avgWordsPerResponse?: number
}

export interface ThemeAICostEstimate {
  responseCount: number
  wordCount: number
  estimatedTokens: number
  estimatedCost: number
  humanCodingCost: number
  humanCodingWeeksLow: number
  humanCodingWeeksHigh: number
  humanComparisonText: string
}

/** Pure function — estimates tokens/cost for a ThemeAI run (spec §7.1). */
export function estimateThemeAICost(input: ThemeAICostEstimateInput): ThemeAICostEstimate {
  const responseCount = Math.max(0, input.responseCount || 0)
  const themeCount = Math.max(0, input.themeCount || 0)
  const avgWords = input.avgWordsPerResponse && input.avgWordsPerResponse > 0
    ? input.avgWordsPerResponse
    : DEFAULT_WORDS_PER_RESPONSE

  const wordCount = responseCount * avgWords

  if (responseCount === 0) {
    return {
      responseCount: 0,
      wordCount: 0,
      estimatedTokens: 0,
      estimatedCost: 0,
      ...humanComparison(0, themeCount),
    }
  }

  const inputTokensPerResponse = avgWords * WORDS_TO_TOKENS + themeCount * FRAME_OVERHEAD_TOKENS_PER_THEME
  let outputTokensPerResponse = BASE_OUTPUT_TOKENS_PER_RESPONSE
  if (input.includeExplanations) {
    outputTokensPerResponse += EXPLANATION_OUTPUT_TOKENS_PER_RESPONSE
  }
  if (input.includeSentiment) {
    outputTokensPerResponse += OPTIONAL_PASS_OUTPUT_TOKENS_PER_RESPONSE
  }
  if (input.includeEmotion) {
    outputTokensPerResponse += OPTIONAL_PASS_OUTPUT_TOKENS_PER_RESPONSE
  }

  const totalInputTokens = inputTokensPerResponse * responseCount
  const totalOutputTokens = outputTokensPerResponse * responseCount
  const estimatedTokens = Math.round(totalInputTokens + totalOutputTokens)

  const estimatedCost = (totalInputTokens * BLENDED_INPUT_RATE_PER_MILLION
    + totalOutputTokens * BLENDED_OUTPUT_RATE_PER_MILLION) / 1_000_000

  return {
    responseCount,
    wordCount,
    estimatedTokens,
    estimatedCost,
    ...humanComparison(responseCount, themeCount),
  }
}

function humanComparison(responseCount: number, themeCount: number) {
  const humanCodingCost = responseCount === 0
    ? 0
    : HUMAN_CODE_FRAME_DEV_COST
      + responseCount * HUMAN_PER_RESPONSE_COST * (1 + HUMAN_QC_RATE)
  const days = responseCount === 0 ? 0 : Math.max(1, responseCount / HUMAN_RESPONSES_PER_DAY)
  const humanCodingWeeksLow = Math.max(1, Math.round((days / 5) * 0.75))
  const humanCodingWeeksHigh = Math.max(humanCodingWeeksLow, Math.round((days / 5) * 1.25))

  const humanComparisonText = responseCount === 0
    ? ''
    : `By the way, if you were to use traditional human coding to develop a code frame`
      + ` (${themeCount || 'a'} themes) and code these responses, it would cost approximately`
      + ` £${Math.round(humanCodingCost).toLocaleString()} and take ${humanCodingWeeksLow}-${humanCodingWeeksHigh}`
      + ` week${humanCodingWeeksHigh === 1 ? '' : 's'}.`

  return { humanCodingCost, humanCodingWeeksLow, humanCodingWeeksHigh, humanComparisonText }
}

/**
 * The unit a Theme AI reader sees money in (no-£ rule, docs/admin.md "No
 * pound sign on a user-facing surface"). A Manager gets no unit and reads
 * pounds, our rate-card cost. Anyone else reads credits always, whatever the
 * customer's display unit, as on the Usage pages: at the customer's credit
 * value from the estimate, or £1 a credit when the estimate carries none
 * (the client-side fallback).
 */
export function themeAIReaderUnit(isManager: boolean, unit?: MoneyUnit): MoneyUnit | undefined {
  if (isManager) return undefined
  return { display_unit: 'credits', credit_value_gbp: unit?.credit_value_gbp || 1 }
}

/**
 * A budget figure on the estimate cards (spend control PR 6d): credits when
 * the unit is credits, pounds otherwise. The caller passes no unit for a
 * Manager and a credits unit for anyone else (themeAIReaderUnit).
 */
export function formatEstimateMoney(gbp: number, unit?: MoneyUnit): string {
  return unit?.display_unit === 'credits' ? formatCredits(toCredits(gbp, unit.credit_value_gbp)) : formatGBP(gbp)
}

/**
 * The team's budget left, as a member may read it: never below £0. A negative
 * remaining figure means the team is working in its headroom, which is hidden
 * from customers (8 Sep call); the page's budget messages say what still works.
 */
export function formatBudgetLeft(gbp: number, unit?: MoneyUnit): string {
  return formatEstimateMoney(Math.max(0, gbp), unit)
}

/** Formats a GBP amount the way the cost dialog / estimate cards expect. */
export function formatGBP(amount: number): string {
  if (amount < 0.01 && amount > 0) return '<£0.01'
  return `£${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export default estimateThemeAICost
